import type { BreakdownTableIndex } from '@/components/common/breakdown-tables/types';
import {
  hasSubDimensions,
  getSubDimensions,
} from '@/components/common/breakdown-tables/indexing';

export interface VisibleDimension {
  primary: string;
  sub: string | null;
  hasChildren: boolean;
  expanded: boolean;
}

export interface BuildVisibleDimensionsParams {
  axis: 'row' | 'column';
  index: BreakdownTableIndex;
  expandedSet: Set<string>;
  fillMissing?: boolean;
  subDimHasData: (primary: string, sub: string) => boolean;
  subDimOrder?: string[];
  hideEmptySubdimensions?: boolean;
}

/**
 * Build visible dimensions for a given axis (row or column)
 * Handles expansion state and filtering of empty sub-dimensions
 *
 * @returns Array of visible dimension coordinates
 */
export function buildVisibleDimensions({
  axis,
  index,
  expandedSet,
  fillMissing = false,
  subDimHasData,
  subDimOrder,
  hideEmptySubdimensions = false,
}: BuildVisibleDimensionsParams): VisibleDimension[] {
  const dimensions: VisibleDimension[] = [];
  const primaryValues =
    axis === 'column' ? index.columnValues : index.rowValues;

  primaryValues.forEach((primary) => {
    const hasChildren = hasSubDimensions(index, axis, primary);
    const expanded = expandedSet.has(primary);

    if (hasChildren && expanded) {
      // Expanded: show primary header first, then sub-dimensions
      const subDims = getSubDimensions(index, axis, primary, {
        subColumnOrder: axis === 'column' ? subDimOrder : undefined,
        subRowOrder: axis === 'row' ? subDimOrder : undefined,
      });

      // Add primary header
      dimensions.push({ primary, sub: null, hasChildren, expanded });

      // Add visible sub-dimensions
      subDims.forEach((sub) => {
        if (sub !== null) {
          // Show sub-dimension if:
          // 1. fillMissing is true (always show), OR
          // 2. hideEmptySubdimensions is false (show by default), OR
          // 3. subDimHasData returns true (has non-zero values)
          const hasData = subDimHasData(primary, sub);
          const shouldShow = fillMissing || !hideEmptySubdimensions || hasData;

          if (shouldShow) {
            dimensions.push({
              primary,
              sub,
              hasChildren: false,
              expanded: false,
            });
          }
        }
      });
    } else {
      // Collapsed or no sub-dimensions: show only primary
      dimensions.push({ primary, sub: null, hasChildren, expanded });
    }
  });

  return dimensions;
}
